import { useEffect, useState } from "react";
import { useSearchParams, Link, Navigate } from "react-router-dom";
import { Calendar, Crown } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DatePicker from "@/components/DatePicker";
import { inr } from "@/lib/brand";

const TABS = [
  { k: "profile", t: "Profile" },
  { k: "bookings", t: "Trips" },
  { k: "wishlist", t: "Wishlist" },
  { k: "membership", t: "Membership" },
];

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "profile";

  if (user === undefined) return <div className="p-10 text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/" />;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="profile-page">
      {/* Pill Tabs */}
      <div className="flex justify-center mb-10">
        <div className="pill-tabs" data-testid="profile-tabs">
          {TABS.map((m) => (
            <button key={m.k} onClick={() => setSp({ tab: m.k })} className={`pill-tab ${tab === m.k ? "active" : ""}`} data-testid={`tab-${m.k}`}>{m.t}</button>
          ))}
        </div>
      </div>

      {tab === "profile" && <ProfileTab user={user} setUser={setUser} />}
      {tab === "bookings" && <BookingsTab />}
      {tab === "wishlist" && <WishlistTab />}
      {tab === "membership" && <MembershipTab />}
    </div>
  );
}

function FloatingInput({ label, required, ...props }) {
  const [focused, setFocused] = useState(false);
  const filled = props.value && String(props.value).length > 0;
  return (
    <label className="block relative">
      <div className={`border rounded-md px-3 pt-5 pb-2 transition ${focused ? "border-stone-900" : "border-stone-300"}`}>
        <div className={`absolute left-3 transition-all pointer-events-none ${focused || filled ? "text-[0.65rem] top-1.5 text-stone-500" : "top-3.5 text-sm text-stone-500"}`}>
          {label}{required && <span className="text-red-500"> *</span>}
        </div>
        <input
          {...props}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          className="w-full outline-none bg-transparent text-sm"
        />
      </div>
    </label>
  );
}

function ProfileTab({ user, setUser }) {
  const [form, setForm] = useState({
    first_name: user.name?.split(" ")[0] || "",
    last_name: user.name?.split(" ").slice(1).join(" ") || "",
    gender: user.gender || "",
    dob: user.dob || "",
    email: user.email,
    city: user.city || "",
    phone: user.phone || "",
  });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setMsg(""); setErr("");
    try {
      const name = [form.first_name, form.last_name].filter(Boolean).join(" ");
      const { data } = await api.put("/auth/profile", { name, phone: form.phone });
      setUser(data);
      setMsg("Profile updated");
    } catch (e) { setErr(formatError(e)); }
  };

  return (
    <form onSubmit={save} className="space-y-4 max-w-xl mx-auto" data-testid="profile-form">
      <FloatingInput label="First Name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="profile-first-name" />
      <FloatingInput label="Last Name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="profile-last-name" />
      <div className="grid grid-cols-2 gap-3">
        <label className="block relative">
          <div className="border rounded-md px-3 pt-5 pb-2 border-stone-300">
            <div className="absolute left-3 top-1.5 text-[0.65rem] text-stone-500">Gender <span className="text-red-500">*</span></div>
            <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="w-full outline-none bg-transparent text-sm" data-testid="profile-gender">
              <option value="">Select</option>
              <option>Male</option><option>Female</option><option>Other</option>
            </select>
          </div>
        </label>
        <label className="block relative">
          <div className="border rounded-md px-3 pt-5 pb-2 border-stone-300">
            <div className="absolute left-3 top-1.5 text-[0.65rem] text-stone-500">Date of birth <span className="text-red-500">*</span></div>
            <DatePicker value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} placeholder="Select date" testid="profile-dob" />
          </div>
        </label>
      </div>
      <FloatingInput label="Email ID" required type="email" value={form.email} disabled data-testid="profile-email" />
      <FloatingInput label="Residential City" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="profile-city" />
      <div className="grid grid-cols-[80px_1fr] gap-3">
        <div className="border border-stone-300 rounded-md px-3 py-3 text-sm flex items-center justify-center">+91</div>
        <FloatingInput label="Mobile Number" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" />
      </div>

      {msg && <div className="text-sm text-emerald-700">{msg}</div>}
      {err && <div className="text-sm text-red-700">{err}</div>}
      <div className="flex justify-center pt-2">
        <button className="btn-primary !rounded-md px-8" data-testid="profile-save">Update</button>
      </div>
    </form>
  );
}

function BookingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/bookings/my").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const today = new Date().toISOString().split("T")[0];
  const filtered = items.filter((b) => {
    if (filter === "upcoming") return b.checkin >= today && b.status !== "cancelled";
    if (filter === "completed") return b.checkout < today && b.status !== "cancelled";
    if (filter === "cancelled") return b.status === "cancelled";
    return true;
  });
  const cancel = async (id) => {
    if (!confirm("Cancel this booking?")) return;
    await api.post(`/bookings/${id}/cancel`);
    load();
  };
  return (
    <div data-testid="bookings-tab">
      <div className="flex gap-2 mb-6 text-sm justify-center flex-wrap">
        {[["all", "All"], ["upcoming", "Upcoming"], ["completed", "Completed"], ["cancelled", "Cancelled"]].map(([k, t]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-4 py-1.5 rounded-full text-sm font-medium ${filter === k ? "bg-stone-900 text-white" : "bg-stone-100 hover:bg-stone-200"}`} data-testid={`bookings-${k}`}>{t}</button>
        ))}
      </div>
      {loading ? <div className="text-stone-500">Loading…</div> : filtered.length === 0 ? (
        <div className="border border-dashed border-stone-300 p-12 text-center text-stone-500 rounded-2xl">
          <Calendar className="w-10 h-10 mx-auto mb-3 text-stone-300" />
          No trips yet. Start exploring!
          <div><Link to="/search" className="btn-outline mt-4">Browse Stays</Link></div>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((b) => (
            <div key={b.id} className="bg-white border border-stone-200 rounded-2xl p-4 grid md:grid-cols-[140px_1fr_auto] gap-4 items-center" data-testid={`booking-${b.id}`}>
              {b.property_image && <img src={b.property_image} alt="" className="w-full md:w-[140px] h-28 object-cover rounded-xl" />}
              <div>
                <div className="text-[0.6rem] text-stone-500 uppercase tracking-wider">{b.booking_number}</div>
                <div className="font-display text-xl mt-0.5">{b.property_title}</div>
                <div className="text-xs text-stone-500 mt-1">{b.room_name} • {b.checkin} → {b.checkout} • {b.guests} guests</div>
                <div className="text-xs mt-2"><span className={`px-2 py-0.5 rounded-full ${b.status === "cancelled" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>{b.status}</span></div>
              </div>
              <div className="text-right">
                <div className="font-display text-xl">{inr(b.amount)}</div>
                <div className="text-xs text-stone-500">{b.payment_status}</div>
                {b.status !== "cancelled" && new Date(b.checkin) > new Date() && (
                  <button onClick={() => cancel(b.id)} className="text-xs text-red-700 underline mt-2" data-testid={`cancel-${b.id}`}>Cancel</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WishlistTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/wishlist").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="wishlist-tab">
      {items.length === 0 ? (
        <div className="border border-dashed border-stone-300 p-12 text-center text-stone-500 rounded-2xl">Your wishlist is empty. <Link to="/search" className="text-stone-900 underline">Discover stays</Link></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((p) => (
            <Link key={p.id} to={`/property/${p.slug}`} className="bg-white border border-stone-200 rounded-2xl overflow-hidden flex hover:shadow-lg transition">
              <img src={p.images?.[0]} alt="" className="w-36 h-36 object-cover" />
              <div className="p-4 flex-1">
                <div className="font-display text-lg">{p.title}</div>
                <div className="text-xs text-stone-500">{p.location}</div>
                <div className="font-display text-lg mt-3">{inr(p.starting_price)}<span className="text-xs text-stone-500 font-medium">/night</span></div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MembershipTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/memberships/my")
      .then((r) => setData(r.data))
      .catch(() => setData({ active: false, membership: null }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-stone-500 text-center py-8">Loading…</div>;

  const memberships = data?.memberships || [];
  const hasActive = memberships.some(m => data.active); // The backend returns active=true if ANY membership is active, but we can also just rely on memberships array being non-empty and active. Actually, the backend returns active=true/false overall.
  
  if (memberships.length === 0) {
    return (
      <div className="text-center py-12" data-testid="membership-tab-empty">
        <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
          <Crown className="w-7 h-7 text-stone-400" />
        </div>
        <div className="font-display text-2xl mb-2">No active membership</div>
        <p className="text-stone-500 mb-6 text-sm">Unlock premium apartments and exclusive perks.</p>
        <Link to="/#membership" onClick={() => setTimeout(() => document.getElementById("membership")?.scrollIntoView({ behavior: "smooth" }), 100)} className="btn-primary">
          Buy Membership
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-4" data-testid="membership-tab">
      {memberships.map((m, idx) => {
        const isActive = data.active; // Since they are returned in the active block or inactive block
        return (
          <div key={idx} className={`rounded-2xl p-6 ${isActive ? "bg-gradient-to-br from-stone-900 to-stone-800 text-white" : "bg-stone-100"}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${isActive ? "bg-white/10 text-amber-300" : "bg-white text-stone-500"}`}>
                <Crown className="w-5 h-5" />
              </div>
              <div>
                <div className={`text-xs uppercase tracking-widest ${isActive ? "text-stone-300" : "text-stone-500"}`}>
                  {isActive ? "Active Membership" : "Expired"}
                </div>
                <div className="font-display text-2xl">{m.plan_name}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className={`text-xs uppercase tracking-widest ${isActive ? "text-stone-300" : "text-stone-500"}`}>Amount</div>
                <div className="font-semibold">{inr(m.amount)}</div>
              </div>
              <div>
                <div className={`text-xs uppercase tracking-widest ${isActive ? "text-stone-300" : "text-stone-500"}`}>Duration</div>
                <div className="font-semibold">{m.duration_months ? `${m.duration_months} months` : "7 days"}</div>
              </div>
              <div>
                <div className={`text-xs uppercase tracking-widest ${isActive ? "text-stone-300" : "text-stone-500"}`}>Purchased</div>
                <div className="font-semibold">{m.purchase_date ? new Date(m.purchase_date).toLocaleDateString() : "—"}</div>
              </div>
              <div>
                <div className={`text-xs uppercase tracking-widest ${isActive ? "text-stone-300" : "text-stone-500"}`}>Valid Till</div>
                <div className="font-semibold">{m.valid_till ? new Date(m.valid_till).toLocaleDateString() : "—"}</div>
              </div>
            </div>
          </div>
        );
      })}
      
      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
        {!data.active && (
          <Link to="/#membership" onClick={() => setTimeout(() => document.getElementById("membership")?.scrollIntoView({ behavior: "smooth" }), 100)} className="btn-outline text-center">
            Renew Membership
          </Link>
        )}
      </div>
    </div>
  );
}
